import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCheckCircle, FiAlertCircle, FiInfo, FiAlertTriangle, FiX, FiClock } from 'react-icons/fi';

const toastTypes = {
  success: {
    icon: FiCheckCircle,
    className: 'bg-gradient-to-r from-emerald-500/10 to-emerald-600/10 border-emerald-500/20 text-emerald-700',
    iconClass: 'text-emerald-500',
    progressClass: 'bg-gradient-to-r from-emerald-500 to-emerald-600',
    shadowClass: 'shadow-[0_4px_12px_-2px_rgba(16,185,129,0.15)]',
  },
  error: {
    icon: FiAlertCircle,
    className: 'bg-gradient-to-r from-rose-500/10 to-rose-600/10 border-rose-500/20 text-rose-700',
    iconClass: 'text-rose-500',
    progressClass: 'bg-gradient-to-r from-rose-500 to-rose-600',
    shadowClass: 'shadow-[0_4px_12px_-2px_rgba(244,63,94,0.15)]',
  },
  info: {
    icon: FiInfo,
    className: 'bg-gradient-to-r from-blue-500/10 to-blue-600/10 border-blue-500/20 text-blue-700',
    iconClass: 'text-blue-500',
    progressClass: 'bg-gradient-to-r from-blue-500 to-blue-600',
    shadowClass: 'shadow-[0_4px_12px_-2px_rgba(59,130,246,0.15)]',
  },
  warning: {
    icon: FiAlertTriangle,
    className: 'bg-gradient-to-r from-amber-500/10 to-amber-600/10 border-amber-500/20 text-amber-700',
    iconClass: 'text-amber-500',
    progressClass: 'bg-gradient-to-r from-amber-500 to-amber-600',
    shadowClass: 'shadow-[0_4px_12px_-2px_rgba(245,158,11,0.15)]',
  },
};

const Toast = ({ message, type = 'info', details, onClose, progress, autoClose }) => {
  const { icon: Icon, className, iconClass, progressClass, shadowClass } = toastTypes[type];

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.3 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.5, transition: { duration: 0.2 } }}
      className={`relative flex items-start space-x-3 w-full max-w-sm rounded-lg border p-4 
        backdrop-blur-sm backdrop-saturate-150 ${className} ${shadowClass}`}
    >
      <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${iconClass}`} />
      <div className="flex-1 space-y-1">
        <p className="font-medium leading-tight">{message}</p>
        {details && (
          <p className="text-sm opacity-80">{details}</p>
        )}
        {autoClose && (
          <div className="flex items-center space-x-1.5 text-xs mt-2 opacity-70">
            <FiClock className="w-3 h-3" />
            <span>Auto-closing in {Math.ceil(autoClose / 1000)}s</span>
          </div>
        )}
      </div>
      <button
        onClick={onClose}
        className="flex-shrink-0 transition-all duration-200 p-1 rounded-full 
          hover:bg-black/5 active:bg-black/10 active:scale-95"
      >
        <FiX className="w-4 h-4" />
      </button>

      {/* Progress bar */}
      {autoClose && (
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: autoClose / 1000, ease: "linear" }}
          className={`absolute bottom-0 left-0 right-0 h-0.5 ${progressClass} opacity-30 rounded-full`}
          style={{ transformOrigin: "0% 50%" }}
        />
      )}
    </motion.div>
  );
};

export default Toast; 